// Hooks TanStack Query para transactions/expenses/incomes.
// Convencao de keys (paralelo ao mobile):
//   ['transactions', 'list', params]
//   ['transactions', 'summary', params]
//   ['expenses', 'list', params]
//   ['expenses', 'detail', id]
//   ['incomes', 'list', params]
//   ['incomes', 'detail', id]
//
// Mutations invalidam keys-prefix relevantes (TanStack matchers tratam
// arrays como prefixo). Ex: invalidar `['transactions']` invalida tudo
// (lista + summary), o que e desejavel apos criar/editar.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateExpensePayload,
  CreateIncomePayload,
  UpdateExpensePayload,
  UpdateIncomePayload,
} from '../types/transactions.types';

import {
  expensesApi,
  incomesApi,
  transactionsApi,
  type ExpensesListParams,
  type IncomesListParams,
  type TransactionsListParams,
  type TransactionsSummaryParams,
} from './transactions.api';

// --------------------------------------------------------------------------
// Transactions (read-only)
// --------------------------------------------------------------------------

export function useTransactionsQuery(params: TransactionsListParams = {}) {
  return useQuery({
    queryKey: ['transactions', 'list', params] as const,
    queryFn: () => transactionsApi.list(params),
    placeholderData: keepPreviousData,
  });
}

export function useTransactionSummaryQuery(params: TransactionsSummaryParams = {}) {
  return useQuery({
    queryKey: ['transactions', 'summary', params] as const,
    queryFn: () => transactionsApi.summary(params),
    placeholderData: keepPreviousData,
  });
}

// --------------------------------------------------------------------------
// Expenses
// --------------------------------------------------------------------------

export function useExpensesQuery(params: ExpensesListParams = {}) {
  return useQuery({
    queryKey: ['expenses', 'list', params] as const,
    queryFn: () => expensesApi.list(params),
    placeholderData: keepPreviousData,
  });
}

export function useExpenseQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['expenses', 'detail', id] as const,
    queryFn: () => {
      if (!id) throw new Error('expense id is required');
      return expensesApi.get(id);
    },
    enabled: !!id,
  });
}

function invalidateAll(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['transactions'] });
  void queryClient.invalidateQueries({ queryKey: ['expenses'] });
  void queryClient.invalidateQueries({ queryKey: ['incomes'] });
  // Phase 10: dashboards agregam transactions/expenses/incomes.
  // Mutations em qualquer um deles tornam KPIs/charts stale.
  void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
}

export function useCreateExpenseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateExpensePayload) => expensesApi.create(body),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useUpdateExpenseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateExpensePayload }) =>
      expensesApi.update(id, body),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useDeleteExpenseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => expensesApi.remove(id),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useDeleteExpenseSeriesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => expensesApi.removeSeries(id),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useRebalanceSplitsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body?: {
        splits?: Array<{ user_id: string; percentage: number }>;
        include_today?: boolean;
      };
    }) => expensesApi.rebalanceSplits(id, body),
    onSuccess: () => invalidateAll(queryClient),
  });
}

// --------------------------------------------------------------------------
// Incomes
// --------------------------------------------------------------------------

export function useIncomesQuery(params: IncomesListParams = {}) {
  return useQuery({
    queryKey: ['incomes', 'list', params] as const,
    queryFn: () => incomesApi.list(params),
    placeholderData: keepPreviousData,
  });
}

export function useIncomeQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['incomes', 'detail', id] as const,
    queryFn: () => {
      if (!id) throw new Error('income id is required');
      return incomesApi.get(id);
    },
    enabled: !!id,
  });
}

export function useCreateIncomeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateIncomePayload) => incomesApi.create(body),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useUpdateIncomeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateIncomePayload }) =>
      incomesApi.update(id, body),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useDeleteIncomeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => incomesApi.remove(id),
    onSuccess: () => invalidateAll(queryClient),
  });
}
